import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  ASSETS_CREATE,
  ASSETS_UPDATE,
  ASSETS_VIEW,
} from "../../assetsPermissions";
import { archivedAssetsSearchSchema, assetsSearchSchema } from "./searchSchemas";
import type { AssetsRouteFactory } from "./assetsRouteContext";

const AssetsListScreen = lazyRouteComponent(
  () => import("../AssetsListScreen"),
  "AssetsListScreen",
);
const AssetFormScreen = lazyRouteComponent(
  () => import("../AssetFormScreen"),
  "AssetFormScreen",
);
const AssetDetailScreen = lazyRouteComponent(
  () => import("../AssetDetailScreen"),
  "AssetDetailScreen",
);
const AssetEditScreen = lazyRouteComponent(
  () => import("../AssetEditScreen"),
  "AssetEditScreen",
);
const ArchivedAssetsListScreen = lazyRouteComponent(
  () => import("../ArchivedAssetsListScreen"),
  "ArchivedAssetsListScreen",
);

export const createAssetsListRoutes: AssetsRouteFactory = ({
  context,
  assets,
  assetTypes,
  companies,
  branches,
}) => {
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id/edit`,
    beforeLoad: requirePermission([ASSETS_UPDATE]),
    component: function AssetEditRouteScreen() {
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(AssetEditScreen, {
        repository: assets,
        basePath: context.basePath,
        assetId: id,
        branches,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id`,
    beforeLoad: requirePermission([ASSETS_VIEW]),
    component: function AssetDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(AssetDetailScreen, {
        repository: assets,
        basePath: context.basePath,
        assetId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: context.basePath,
      validateSearch: assetsSearchSchema,
      beforeLoad: requirePermission([ASSETS_VIEW]),
      component: () =>
        createElement(AssetsListScreen, {
          repository: assets,
          basePath: context.basePath,
        }),
    }),
    // 'create' and 'archived' are literal segments, registered before the
    // '$id' routes so neither is ever captured as an id.
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/create`,
      beforeLoad: requirePermission([ASSETS_CREATE]),
      component: () =>
        createElement(AssetFormScreen, {
          repository: assets,
          assetTypesRepository: assetTypes,
          basePath: context.basePath,
          companies,
          branches,
        }),
    }),
    editRoute,
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/archived`,
      validateSearch: archivedAssetsSearchSchema,
      beforeLoad: requirePermission([ASSETS_VIEW]),
      component: () =>
        createElement(ArchivedAssetsListScreen, {
          repository: assets,
          basePath: context.basePath,
        }),
    }),
    // Bare '$id' is the most general path here -- kept last.
    detailRoute,
  ];
};
